/**
 * What OS is doing with a live call's questions, as the conversation draws it
 * (stage 8 phase 6, the phone's `asking`, `progress`, `queued` and
 * `notReached` channels in `hooks/use-assistant-live.ts`):
 *
 * - **asking**: the person's question, the moment the voice hands it to OS.
 *   The conversation draws it as their spoken bubble at once, and the `turn`
 *   that answers it carries the same message id and takes its place.
 * - **queued**: a question the voice took while OS was still on the one
 *   before. It runs next; an `asking` with `queued` false and the same id
 *   says it has started. One waits at most: a newer one takes its place. A
 *   version-2 call says it with `next` (the one visible Next, typed or
 *   said); the rest of its words are the live transcript's
 *   (`liveTranscriptStep`), never this.
 * - **progress**: the newest tool OS started on the question, worded by the
 *   phone's `progressLabelOf` for the working line.
 * - **notReached**: the voice promised to look something up and called
 *   nothing. The conversation says so, with what the voice said, and Ask
 *   again sends the person's question through the call.
 *
 * Pure, so `bun test` reads every step of it.
 */
import type { AssistantLiveServerMessage } from '@alluzo/shared';

export type CallQuestion = {
  readonly messageId: string;
  readonly text: string;
};
export type CallProgress = { readonly tool: string; readonly input: unknown };
export type CallMiss = { readonly heard: string; readonly said: string };

export type CallWork = {
  readonly asking: CallQuestion | null;
  readonly queued: CallQuestion | null;
  readonly progress: CallProgress | null;
  readonly notReached: CallMiss | null;
};

export const NO_CALL_WORK: CallWork = {
  asking: null,
  queued: null,
  progress: null,
  notReached: null,
};

export type CallWorkNow = {
  /**
   * The `turn` is small talk the voice answered by itself while OS works on
   * another line (`isVoiceFold`): OS's question, and its steps, go on.
   */
  readonly fold: boolean;
  /** OS is still answering a question when the voice's slip arrives: its work goes on. */
  readonly working: boolean;
};

const NOW: CallWorkNow = { fold: false, working: false };

/** The work after one frame from the call. A frame that says nothing about OS's work changes nothing. */
export const callWorkAfter = (work: CallWork, message: AssistantLiveServerMessage, now: CallWorkNow = NOW): CallWork => {
  switch (message.type) {
    case 'asking': {
      const question = { messageId: message.messageId, text: message.text };
      if (message.queued) return { ...work, queued: question };
      return {
        asking: question,
        queued: work.queued?.messageId === message.messageId ? null : work.queued,
        progress: null,
        notReached: null,
      };
    }
    case 'progress':
      return {
        ...work,
        progress: { tool: message.tool, input: message.input },
      };
    case 'turn':
      if (now.fold) return work;
      return { ...work, asking: null, progress: null, notReached: null };
    case 'not_reached': {
      const notReached = { heard: message.heard, said: message.said };
      return now.working ? { ...work, notReached } : { ...work, asking: null, progress: null, notReached };
    }
    // A version-2 call's one visible Next: typed or said, it waits in the same slot, and `id: null` clears it.
    case 'next':
      return { ...work, queued: message.id === null ? null : { messageId: message.id, text: message.text } };
    default:
      return work;
  }
};

/**
 * Whether the question's own bubble still stands in the conversation: until
 * the thread holds a message under its id (the `turn` that answered it).
 */
export const askingShown = (asking: CallQuestion | null, messageIds: ReadonlySet<string>): asking is CallQuestion =>
  asking !== null && asking.text.trim() !== '' && !messageIds.has(asking.messageId);
